import React, { useEffect, useState, useRef } from "react"
// Reuse category keyword tags styles for consistent look
import "../CategoryAnalysis/style/keywordTags.scss"
import minecraftImg from "../../assets/images/articles/image.png"
import PlatformIcon from "../../components/Common/PlatformIcon"
import { useOptimizedKeywordTracking } from "../../pages/NewKeywordTrackingUI/OptimizedKeywordTrackingUIContainer"
import { useNavigate, useLocation } from "react-router-dom"
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react"
import { userProfileApi } from "../../services/userProfileApi"
import AppsTrackingPlanModal from "../Common/Modal/AppsTrackingPlanModal"
import SkeletonCard from "../../pages/KeywordTracking/components/SkeletonCard"
import { Info } from "lucide-react"
import { useSearchProgress } from "../../hooks/useSearchProgress";
import SearchProgressModal from "../Common/Modal/SearchProgressModal";
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics";

// Game mode icon components
const SingleUserIcon = ({ size = 16, color = "#D2D2D2" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M20.59 22C20.59 18.13 16.74 15 12 15C7.26 15 3.41 18.13 3.41 22" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const MultiplayerIcon = ({ size = 16, color = "#D2D2D2" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const CoopIcon = ({ size = 16, color = "#D2D2D2" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 21V19C17 18.0807 16.5786 17.2044 15.8284 16.4542C15.0783 15.704 14.0609 15.2826 13 15.2826H5C3.93913 15.2826 2.92172 15.704 2.17157 16.4542C1.42143 17.2044 1 18.0807 1 19V21" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M23 21V19C23 18.0807 22.5786 17.2044 21.8284 16.4542C21.0783 15.704 20.0609 15.2826 19 15.2826C18.6144 15.2826 18.2343 15.3467 17.8769 15.4715C17.5195 15.5963 17.1903 15.7794 16.9046 16.0123C16.619 16.2451 16.3811 16.5241 16.2023 16.836C16.0235 17.148 15.9066 17.488 15.8572 17.8398C15.8078 18.1916 15.8268 18.5494 15.9128 18.8939C15.9988 19.2383 16.1502 19.5639 16.3592 19.8545C16.5682 20.1451 16.8311 20.3958 17.1347 20.5941C17.4383 20.7924 17.7772 20.9348 18.1337 21.0141C18.4902 21.0934 18.8584 21.1082 19.2197 21.0578" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19 11C21.2091 11 23 9.20914 23 7C23 4.79086 21.2091 3 19 3C16.7909 3 15 4.79086 15 7C15 9.20914 16.7909 11 19 11Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

// Helper function to get game mode icon
const getGameModeIcon = (mode) => {
  if (!mode) return null

  const modeLower = mode.toString().toLowerCase().trim().replace(/[_\s-]/g, '')

  // Single User variations: "single", "single user", "singleuser"
  if (modeLower.includes('single')) {
    return <SingleUserIcon size={18} color="#D2D2D2" />
  }
  // Multiplayer variations: "multiplayer", "multi"
  else if (modeLower.includes('multiplayer') || modeLower.includes('multi')) {
    return <MultiplayerIcon size={18} color="#D2D2D2" />
  }
  // Co-op variations: "co-op", "coop", "cooperative"
  else if (modeLower.includes('coop') || modeLower.includes('cooperative')) {
    return <CoopIcon size={18} color="#D2D2D2" />
  }

  return null
}

const KeywordSearchResults = () => {
  const {
    filterChips,
    activeChip,
    setActiveChip,
    filteredSearchResults,
    setSelectedChip,
    selectedChip,
    isLoading,
    isSearchMode,
    setIsSearchMode,
    clearSearch,
    searchValue,
    submittedKeyword,
    isBackendProcessing,
    currentPage,
    totalPages,
    handlePageChange,
    showKeywordOnboarding,
    keywordOnboardingStep,
    setKeywordOnboardingStep,
    completeKeywordOnboarding,
    skipAllTutorials,
  } = useOptimizedKeywordTracking()
  const navigate = useNavigate()
  const location = useLocation()
  const { trackEvent } = useAnalytics()

  // Mark tutorial as completed when user leaves the page while tutorial is active
  // This component only exists on keyword explorer page, so cleanup runs when navigating away
  useEffect(() => {
    // Cleanup function runs when component unmounts (user navigates away)
    return () => {
      try {
        const tutorialSeen = sessionStorage.getItem('keywordExplorerOnboardingSeen') === 'true';
        const currentStep = sessionStorage.getItem('keywordTutorialCurrentStep');

        // If tutorial is active (not seen and has a current step), mark it as completed
        if (!tutorialSeen && currentStep && (currentStep === '0' || currentStep === '1' || currentStep === '2' || currentStep === '3' || currentStep === '4')) {
          sessionStorage.setItem('keywordExplorerOnboardingSeen', 'true');
          sessionStorage.removeItem('keywordTutorialCurrentStep');
          window.dispatchEvent(new CustomEvent('keywordTutorialStep', { detail: { step: -1 } }));

          // Try to mark as completed via API (non-blocking)
          fetch('/api/onboarding/complete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ page: 'keyword_explorer' })
          }).catch(() => null);
        }
      } catch (e) {
        // ignore storage errors
      }
    };
  }, []);

  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [isExplorerOnly, setIsExplorerOnly] = useState(false)
  const [viewType, setViewType] = useState('table') // 'card' or 'table'
  const [showViewDropdown, setShowViewDropdown] = useState(false)
  const [expandedGenres, setExpandedGenres] = useState(new Set()) // Track which rows have expanded genres
  const chipsSectionRef = useRef(null)
  const lastChipRef = useRef(null)

  const activeKeyword = (isSearchMode ? submittedKeyword : selectedChip) || ''
  const hasActiveKeyword = !!activeKeyword.trim()

  // Every result click (table row, card, CTAs) goes through here so the
  // explorer's own result-click event always fires alongside navigation.
  const openAppDetail = (result) => {
    trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_EXPLORER_APP_SELECTED, {
      app_id: result.item_id,
      app_name: result.name,
      keyword: activeKeyword,
      platform: result.platform,
    })
    navigate(`/xr/app/${(result.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}?id=${result.item_id}`, {
      state: { app_id: result.item_id, selected_tab: 2, app_name: result.name, source_surface: "keyword_explorer", search_keyword: activeKeyword },
    })
  }

  const { progress, showProgressModal, cancel: cancelSearchProgress } = useSearchProgress({
    loading: isBackendProcessing && hasActiveKeyword,
  });

  const handleCancelSearch = () => {
    cancelSearchProgress();
    // Clears every piece of search state (including `selectedChip`, which was
    // previously left stale here and caused the poll to silently resume on the
    // last-selected chip instead of actually stopping) so the UI is immediately
    // ready to accept a new request.
    clearSearch();
  };

  useEffect(() => {
    let cancelled = false
      ; (async () => {
        try {
          const res = await userProfileApi.fetchCurrentPlan()
          if (cancelled) return
          // If API returns a product object, user has a paid plan. If product is null -> Explorer (free)
          const hasProduct = !!(res && res.data && res.data.product)
          setIsExplorerOnly(!hasProduct)
        } catch (err) {
          // On error, assume non-explorer to avoid hiding data for paid users. Default to false.
          console.error('Error fetching current plan for premium gating:', err)
          setIsExplorerOnly(false)
        }
      })()
    return () => { cancelled = true }
  }, [])

  // Position highlight boxes and arrows dynamically for chips (step 2)
  useEffect(() => {
    if (!showKeywordOnboarding || keywordOnboardingStep !== 2) return;

    const updatePositions = () => {
      if (!chipsSectionRef.current) return;

      // Find the last keyword chip first — while the real keywords are
      // still loading, only the skeleton placeholders exist yet, so fall
      // back to the last one of those (and finally to the section itself)
      // instead of leaving the tutorial box unpositioned. Once the real
      // data arrives, `filterChips` changes and this effect re-runs to
      // anchor against the actual last chip instead.
      const keywordChips = chipsSectionRef.current?.querySelectorAll('.keyword-tag, .keyword-tag-skeleton');
      const lastChip = keywordChips && keywordChips.length > 0
        ? keywordChips[keywordChips.length - 1]
        : chipsSectionRef.current;

      if (!lastChip) return;

      const lastChipRect = lastChip.getBoundingClientRect();

      // Highlight only the last keyword chip
      const highlightBox = document.querySelector('.kt-highlight-box--chips');
      if (highlightBox) {
        highlightBox.style.top = `${lastChipRect.top - 8}px`;
        highlightBox.style.left = `${lastChipRect.left - 8}px`;
        highlightBox.style.width = `${lastChipRect.width + 16}px`;
        highlightBox.style.height = `${lastChipRect.height + 16}px`;
      }

      const explanationBox = document.querySelector('.kt-explanation-box--chips');
      if (explanationBox) {
        // Reset first so we can measure correctly
        explanationBox.style.top = '0px';
        explanationBox.style.left = '0px';

        requestAnimationFrame(() => {
          const boxRect = explanationBox.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          const isMobileNow = viewportWidth <= 768;

          if (isMobileNow) {
            // Mobile: Position box below the chips, centered
            const gap = 16;
            let boxTop = lastChipRect.bottom + gap;
            const boxLeft = 16; // Use CSS left: 16px from media query

            // Ensure box stays within viewport
            if (boxTop + boxRect.height > viewportHeight - 16) {
              boxTop = Math.max(16, viewportHeight - boxRect.height - 16);
            }

            explanationBox.style.top = `${boxTop}px`;
          } else {
            // Desktop/Tablet: Position above the chips, horizontally centered
            const verticalGap = 32; // larger gap so tutorial box sits higher above the chip
            let boxTop = lastChipRect.top - verticalGap - boxRect.height;
            let boxLeft = lastChipRect.left + (lastChipRect.width / 2) - (boxRect.width / 2);

            // Ensure box stays within viewport bounds
            if (boxLeft < 16) {
              boxLeft = 16;
            } else if (boxLeft + boxRect.width > viewportWidth - 16) {
              boxLeft = viewportWidth - boxRect.width - 16;
            }
            if (boxTop < 16) {
              boxTop = 16;
            } else if (boxTop + boxRect.height > viewportHeight - 16) {
              boxTop = viewportHeight - boxRect.height - 16;
            }

            explanationBox.style.top = `${boxTop}px`;
            explanationBox.style.left = `${boxLeft}px`;

            // Arrow: vertical line from tutorial box down to the keyword chip
            const arrow = document.querySelector('.kt-arrow-to-chips');
            if (arrow) {
              const actualBoxRect = explanationBox.getBoundingClientRect();

              const arrowX = actualBoxRect.left + (actualBoxRect.width / 2);
              const arrowTop = actualBoxRect.top + actualBoxRect.height; // bottom of box
              const arrowBottom = lastChipRect.top - 4; // just above chip highlight
              const height = Math.max(arrowBottom - arrowTop, 16);

              // Position arrow vertically from box down to the chip
              arrow.style.left = `${arrowX}px`;
              arrow.style.top = `${arrowTop}px`;
              arrow.style.width = `3px`;
              arrow.style.height = `${height}px`;
              arrow.style.background = '#F6255A';
              arrow.style.transform = 'none';
              arrow.style.transformOrigin = '0 50%';
              arrow.style.display = 'block';
              arrow.style.visibility = 'visible';
              arrow.style.opacity = '1';
              arrow.style.position = 'fixed';
              arrow.style.overflow = 'visible';
            }
          }
        });
      }
    };

    updatePositions();
    window.addEventListener('resize', updatePositions);
    window.addEventListener('scroll', updatePositions, true);

    return () => {
      window.removeEventListener('resize', updatePositions);
      window.removeEventListener('scroll', updatePositions, true);
    };
  }, [showKeywordOnboarding, keywordOnboardingStep, filterChips]);

  const Pagination = ({ currentPage, totalPages, onPageChange, isLoading }) => {
    if (totalPages <= 1) return null

    const getPageNumbers = () => {
      const pages = []
      let start = Math.max(1, currentPage - 2)
      let end = Math.min(totalPages, currentPage + 2)

      if (start > 1) {
        pages.push(1)
        if (start > 2) pages.push("...")
      }

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      if (end < totalPages) {
        if (end < totalPages - 1) pages.push("...")
        pages.push(totalPages)
      }

      return pages
    }

    const handlePageClick = (page) => {
      if (typeof page === "number" && page !== currentPage && !isLoading) {
        onPageChange(page)
      }
    }

    return (
      <div className="ksr-pagination-container">
        <div className="ksr-pagination-wrapper">
          <button
            className="ksr-pagination-btn ksr-pagination-nav"
            disabled={currentPage <= 1 || isLoading}
            onClick={() => handlePageClick(currentPage - 1)}
          >
            <ChevronLeft size={16} style={{ pointerEvents: 'none' }} /> Previous
          </button>

          <div className="ksr-pagination-numbers">
            {getPageNumbers().map((page, index) => (
              <button
                key={index}
                className={`ksr-pagination-btn ${page === currentPage ? "active" : ""
                  } ${page === "..." ? "ellipsis" : ""}`}
                disabled={page === "..." || isLoading}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handlePageClick(page)
                }}
              >
                {page}
              </button>
            ))}
          </div>

          <button
            className="ksr-pagination-btn ksr-pagination-nav"
            disabled={currentPage >= totalPages || isLoading}
            onClick={() => handlePageClick(currentPage + 1)}
          >
            Next <ChevronRight size={16} style={{ pointerEvents: 'none' }} />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="ksr-root">

      <SearchProgressModal
        show={showProgressModal}
        progress={progress}
        title="Analyzing keyword insights"
        onCancel={handleCancelSearch}
        notifyByEmail
      />


      {/* Mirror Category 'keywords-section' design for consistent look */}
      <div className="keywords-section">
        <div style={{ marginBottom: 16 }}>
          <h2 className="publisher-intel-title" style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 500, color: 'rgba(255,255,255,0.8)' }}>
            Commonly Searched Keywords
          </h2>
        </div>

        {filterChips.length > 0 ? (
          <div ref={chipsSectionRef} className="keyword-tags" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, position: 'relative' }}>
            {showKeywordOnboarding && keywordOnboardingStep === 2 && (
              <>
                <div className="kt-backdrop-overlay"></div>
                <div className="kt-highlight-box kt-highlight-box--chips"></div>
              </>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, flex: 1 }}>
              {filterChips.map((chip, idx) => {
                const isLastChip = idx === filterChips.length - 1;
                return (
                  <span
                    key={chip.label}
                    ref={isLastChip ? lastChipRef : null}
                    className={`keyword-tag ${activeChip === idx ? 'active' : ''} ${isLastChip && showKeywordOnboarding && keywordOnboardingStep === 2 ? 'kt-highlighted-section' : ''}`}
                    onClick={() => {
                      setActiveChip(idx)
                      setSelectedChip(chip.label)
                      setIsSearchMode(false)
                    }}
                  >
                    {chip.label.charAt(0).toUpperCase() + chip.label.slice(1)}
                  </span>
                )
              })}
            </div>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <button
                onClick={() => setShowViewDropdown(!showViewDropdown)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  color: '#fff',
                  fontSize: 14,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onBlur={() => setTimeout(() => setShowViewDropdown(false), 200)}
              >
                <span>{viewType === 'card' ? 'Card View' : 'List View'}</span>
                <ChevronDown size={16} style={{ transition: 'transform 0.2s ease', transform: showViewDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }} />
              </button>
              {showViewDropdown && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: 8,
                    background: '#1a1d29',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 8,
                    padding: '4px 0',
                    minWidth: 150,
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                  }}
                >
                  <button
                    onClick={() => {
                      setViewType('card')
                      trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_EXPLORER_VIEW_TYPE_CHANGED, { view_type: 'card' })
                      setShowViewDropdown(false)
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      background: viewType === 'card' ? 'rgba(246,37,90,0.15)' : 'transparent',
                      border: 'none',
                      color: '#fff',
                      fontSize: 14,
                      textAlign: 'left',
                      cursor: 'pointer',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (viewType !== 'card') e.currentTarget.style.background = 'rgba(255,255,255,0.05)'
                    }}
                    onMouseLeave={(e) => {
                      if (viewType !== 'card') e.currentTarget.style.background = 'transparent'
                    }}
                  >
                    Card View
                  </button>
                  <button
                    onClick={() => {
                      setViewType('table')
                      trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_EXPLORER_VIEW_TYPE_CHANGED, { view_type: 'table' })
                      setShowViewDropdown(false)
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      background: viewType === 'table' ? 'rgba(246,37,90,0.15)' : 'transparent',
                      border: 'none',
                      color: '#fff',
                      fontSize: 14,
                      textAlign: 'left',
                      cursor: 'pointer',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (viewType !== 'table') e.currentTarget.style.background = 'rgba(255,255,255,0.05)'
                    }}
                    onMouseLeave={(e) => {
                      if (viewType !== 'table') e.currentTarget.style.background = 'transparent'
                    }}
                  >
                    List View
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          // Same ref as the loaded branch above — while chips are still
          // loading (skeletons only, no real .keyword-tag yet), the step-2
          // tutorial's positioning effect needs SOME anchored container to
          // measure against, or it falls back to wherever this section
          // happens to sit in the page's normal flow (off past the bottom
          // of the viewport, since the box is position:fixed and never
          // gets a top/left set).
          <div ref={chipsSectionRef} className="keyword-tags" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, flex: 1 }}>
              {Array.from({ length: 6 }).map((_, idx) => (
                <div key={idx} className="keyword-tag-skeleton" />
              ))}
            </div>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <button
                onClick={() => setShowViewDropdown(!showViewDropdown)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  color: '#fff',
                  fontSize: 14,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onBlur={() => setTimeout(() => setShowViewDropdown(false), 200)}
              >
                <span>{viewType === 'card' ? 'Card View' : 'List View'}</span>
                <ChevronDown size={16} style={{ transition: 'transform 0.2s ease', transform: showViewDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }} />
              </button>
              {showViewDropdown && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: 8,
                    background: '#1a1d29',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 8,
                    padding: '4px 0',
                    minWidth: 150,
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                  }}
                >
                  <button
                    onClick={() => {
                      setViewType('card')
                      trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_EXPLORER_VIEW_TYPE_CHANGED, { view_type: 'card' })
                      setShowViewDropdown(false)
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      background: viewType === 'card' ? 'rgba(246,37,90,0.15)' : 'transparent',
                      border: 'none',
                      color: '#fff',
                      fontSize: 14,
                      textAlign: 'left',
                      cursor: 'pointer',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (viewType !== 'card') e.currentTarget.style.background = 'rgba(255,255,255,0.05)'
                    }}
                    onMouseLeave={(e) => {
                      if (viewType !== 'card') e.currentTarget.style.background = 'transparent'
                    }}
                  >
                    Card View
                  </button>
                  <button
                    onClick={() => {
                      setViewType('table')
                      trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_EXPLORER_VIEW_TYPE_CHANGED, { view_type: 'table' })
                      setShowViewDropdown(false)
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      background: viewType === 'table' ? 'rgba(246,37,90,0.15)' : 'transparent',
                      border: 'none',
                      color: '#fff',
                      fontSize: 14,
                      textAlign: 'left',
                      cursor: 'pointer',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (viewType !== 'table') e.currentTarget.style.background = 'rgba(255,255,255,0.05)'
                    }}
                    onMouseLeave={(e) => {
                      if (viewType !== 'table') e.currentTarget.style.background = 'transparent'
                    }}
                  >
                    List View
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        {showKeywordOnboarding && keywordOnboardingStep === 2 && (
          <div className="kt-explanation-box kt-explanation-box--chips">
            <button
              type="button"
              className="kt-skip-all-btn"
              onClick={() => {
                try {
                  skipAllTutorials()
                  sessionStorage.removeItem('keywordTutorialCurrentStep')
                  window.dispatchEvent(new CustomEvent('keywordTutorialStep', { detail: { step: -1 } }))
                } catch (e) { }
              }}
            >
              Skip All
            </button>
            <div className="kt-explanation-text">
              Pick a <strong>keyword chip</strong> to quickly load popular search terms.
            </div>
            <div className="kt-explanation-arrow kt-arrow-to-chips kt-arrow-right"></div>
            <div className="kt-explanation-nav">
              <button
                type="button"
                className="kt-nav-btn kt-nav-btn--prev"
                onClick={() => {
                  setKeywordOnboardingStep(1)
                  sessionStorage.setItem('keywordTutorialCurrentStep', '1')
                  window.dispatchEvent(new CustomEvent('keywordTutorialStep', { detail: { step: 1 } }))
                }}
              >
                &lt; Prev
              </button>
              <button
                type="button"
                className="kt-nav-btn kt-nav-btn--next"
                onClick={() => {
                  // Move to profile tutorial step (step 3)
                  try {
                    setKeywordOnboardingStep(3)
                    sessionStorage.setItem('keywordTutorialCurrentStep', '3')
                    window.dispatchEvent(new CustomEvent('keywordTutorialStep', { detail: { step: 3 } }))
                  } catch (e) { }
                }}
              >
                Next &gt;
              </button>
            </div>
          </div>
        )}

        <p style={{
          margin: '10px 0 0',
          fontSize: 11,
          color: 'rgba(255,255,255,0.35)',
          fontStyle: 'italic',
          letterSpacing: '0.1px',
        }}>
          * Rankings are based on the keyword's position on the respective platform (Steam / Metastore).
        </p>
      </div>

      {viewType === 'table' ? (
        // Table View
        <div style={{ marginTop: 24 }}>
          {!hasActiveKeyword ? (
            <div className="ksr-no-results">
              <div className="ksr-no-results-text">
                Start by searching a keyword or selecting a trending keyword to see results.
              </div>
            </div>
          ) : isLoading || isBackendProcessing ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>Loading...</div>
          ) : filteredSearchResults.length > 0 ? (
            <div className="ksr-table-container" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', width: '100%' }}>
              <style>{`
                .ksr-table-container {
                  width: 100%;
                  margin-top: 20px;
                  overflow-x: auto;
                  -webkit-overflow-scrolling: touch;
                }
                
                .ksr-table-container table {
                  width: 100%;
                  min-width: 900px;
                  border-collapse: collapse;
                  background: rgba(255,255,255,0.02);
                  border-radius: 8px;
                  overflow: hidden;
                }
                
                .ksr-table-container th,
                .ksr-table-container td {
                  white-space: nowrap;
                }
                
                .ksr-table-game-image {
                  width: 48px;
                  height: 48px;
                }
                
                .ksr-table-container td:nth-child(2) {
                  max-width: 300px;
                  width: 300px;
                }
                
                @media (max-width: 1024px) {
                  .ksr-table-container td:nth-child(2) {
                    max-width: 250px;
                    width: 250px;
                  }
                }
                
                @media (max-width: 768px) {
                  .ksr-table-container td:nth-child(2) {
                    max-width: 200px;
                    width: 200px;
                  }
                }
                
                @media (max-width: 480px) {
                  .ksr-table-container td:nth-child(2) {
                    max-width: 180px;
                    width: 180px;
                  }
                }
                
                @media (max-width: 375px) {
                  .ksr-table-container td:nth-child(2) {
                    max-width: 150px;
                    width: 150px;
                  }
                }
                
                @media (max-width: 1024px) {
                  .ksr-table-container table {
                    min-width: 850px;
                  }
                  
                  .ksr-table-container th,
                  .ksr-table-container td {
                    padding: 12px 10px !important;
                    font-size: 13px !important;
                  }
                  
                  .ksr-table-game-image {
                    width: 40px;
                    height: 40px;
                  }
                }
                
                @media (max-width: 768px) {
                  .ksr-table-container {
                    margin: 0 -16px;
                    width: calc(100% + 32px);
                    padding: 0 16px 20px;
                  }
                  
                  .ksr-table-container table {
                    min-width: 800px;
                  }
                  
                  .ksr-table-container th,
                  .ksr-table-container td {
                    padding: 12px 10px !important;
                    font-size: 13px !important;
                  }
                  
                  .ksr-table-container th:first-child,
                  .ksr-table-container td:first-child {
                    padding-left: 16px !important;
                  }
                  
                  .ksr-table-container th:last-child,
                  .ksr-table-container td:last-child {
                    padding-right: 16px !important;
                  }
                  
                  .ksr-table-game-image {
                    width: 36px;
                    height: 36px;
                  }
                }
                
                @media (max-width: 480px) {
                  .ksr-table-container {
                    margin: 0 -12px;
                    width: calc(100% + 24px);
                    padding: 0 12px 16px;
                  }
                  
                  .ksr-table-container table {
                    min-width: 700px;
                  }
                  
                  .ksr-table-container th,
                  .ksr-table-container td {
                    padding: 10px 8px !important;
                    font-size: 12px !important;
                  }
                  
                  .ksr-table-container th:first-child,
                  .ksr-table-container td:first-child {
                    padding-left: 12px !important;
                  }
                  
                  .ksr-table-container th:last-child,
                  .ksr-table-container td:last-child {
                    padding-right: 12px !important;
                  }
                  
                  .ksr-table-game-image {
                    width: 32px;
                    height: 32px;
                  }
                }
                
                @media (max-width: 375px) {
                  .ksr-table-container {
                    margin: 0 -8px;
                    width: calc(100% + 16px);
                    padding: 0 8px 12px;
                  }
                  
                  .ksr-table-container table {
                    min-width: 650px;
                  }
                  
                  .ksr-table-container th,
                  .ksr-table-container td {
                    padding: 8px 6px !important;
                    font-size: 11px !important;
                  }
                  
                  .ksr-table-container th:first-child,
                  .ksr-table-container td:first-child {
                    padding-left: 8px !important;
                  }
                  
                  .ksr-table-container th:last-child,
                  .ksr-table-container td:last-child {
                    padding-right: 8px !important;
                  }
                  
                  .ksr-table-game-image {
                    width: 28px;
                    height: 28px;
                  }
                }
              `}</style>
              <style>{`
                /* Tutorial backdrop - dims the entire page (match header/profile tutorial design) */
                .kt-backdrop-overlay{
                  position: fixed;
                  top: 0;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  background: rgba(0, 0, 0, 0.5);
                  z-index: 999;
                  /* Block interaction with everything under the backdrop */
                  pointer-events: auto;
                  /* No blur so keyword chips and tutorial content stay crisp; dimming handled via background + highlight box */
                }

                /* Highlight box around the element (match header/profile tutorial design) */
                .kt-highlight-box{
                  position: fixed;
                  border: 2px solid rgba(246, 37, 90, 0.8);
                  border-radius: 15px;
                  pointer-events: none;
                  z-index: 1000;
                  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
                  animation: kt-pulse 2s ease-in-out infinite;
                }
                @keyframes kt-pulse {
                  0%, 100% { border-color: rgba(246, 37, 90, 0.6); }
                  50% { border-color: rgba(246, 37, 90, 1); }
                }
                .kt-highlighted-section{
                  position: relative;
                  z-index: 1001;
                }

                /* Explanation box with arrow (match header/profile tutorial design) */
                .kt-explanation-box{
                  position: fixed;
                  background: #fff;
                  border: 1px solid #ddd;
                  border-radius: 12px;
                  padding: 20px 16px 12px;
                  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
                  z-index: 1002;
                  min-width: 280px;
                  max-width: 320px;
                  box-sizing: border-box;
                }
                .kt-explanation-box--chips{
                  /* positioned dynamically via JS */
                }
                .kt-skip-all-btn{
                  position: absolute;
                  top: 8px;
                  right: 8px;
                  background: transparent;
                  border: none;
                  color: #999;
                  font-size: 12px;
                  cursor: pointer;
                  padding: 6px 8px;
                  min-height: 32px;
                  min-width: 32px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  border-radius: 4px;
                  transition: all 0.2s;
                }
                .kt-skip-all-btn:hover,
                .kt-skip-all-btn:focus{
                  color: #F6255A;
                  background: rgba(246, 37, 90, 0.1);
                  outline: none;
                }
                .kt-skip-all-btn:active{
                  transform: scale(0.95);
                }
                .kt-explanation-text{
                  font-size: 14px;
                  color: #333;
                  line-height: 1.6;
                  margin-top: 15px;
                  margin-bottom: 8px;
                  word-wrap: break-word;
                  white-space: normal;
                }
                /* Center and push further down the chips tutorial text inside its box (clear Skip All in top-right) */
                .kt-explanation-box--chips .kt-explanation-text{
                  text-align: center;
                  margin-top: 40px;
                }
                .kt-explanation-text strong{
                  color: #F6255A;
                  font-weight: 600;
                }
                .kt-explanation-arrow{
                  position: fixed;
                  background: #F6255A;
                  z-index: 1003;
                  pointer-events: none;
                }
                .kt-arrow-to-chips{
                  /* Positioned dynamically via JavaScript */
                }
                .kt-explanation-arrow::after{
                  content: '';
                  position: absolute;
                  top: 50%;
                  width: 0;
                  height: 0;
                  border-top: 6px solid transparent;
                  border-bottom: 6px solid transparent;
                  transform: translateY(-50%);
                }
                /* Chips step: box above the chips, arrow at bottom center pointing down towards the chip */
                .kt-arrow-to-chips::after{
                  top: 100%;
                  left: 50%;
                  transform: translateX(-50%);
                  border-top: 10px solid #F6255A;
                  border-left: 6px solid transparent;
                  border-right: 6px solid transparent;
                }
                .kt-explanation-nav{
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-top: 8px;
                  padding-top: 10px;
                  border-top: 1px solid #eee;
                  gap: 8px;
                }
                .kt-nav-btn{
                  background: transparent;
                  border: none;
                  color: #666;
                  font-size: 14px;
                  cursor: pointer;
                  padding: 8px 12px;
                  min-height: 44px;
                  min-width: 60px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  border-radius: 6px;
                  transition: all 0.2s;
                  flex: 1;
                  font-weight: 500;
                }
                .kt-nav-btn:hover,
                .kt-nav-btn:focus{
                  color: #F6255A;
                  background: rgba(246, 37, 90, 0.1);
                  outline: none;
                }
                .kt-nav-btn:active{
                  transform: scale(0.97);
                }
                .kt-nav-btn--next{
                  font-weight: 600;
                  color: #F6255A;
                  background: rgba(246, 37, 90, 0.1);
                }
                .kt-nav-btn--next:hover,
                .kt-nav-btn--next:focus{
                  background: rgba(246, 37, 90, 0.2);
                }

                /* Tablet styles (768px - 1024px) */
                @media (max-width: 1024px) and (min-width: 769px) {
                  .kt-explanation-box {
                    max-width: 300px;
                    padding: 18px 14px 10px;
                  }
                  .kt-explanation-text {
                    font-size: 13px;
                  }
                }

                /* Mobile styles */
                @media (max-width: 768px) {
                  .kt-explanation-box {
                    min-width: calc(100vw - 32px);
                    max-width: calc(100vw - 32px);
                    width: calc(100vw - 32px);
                    left: 16px !important;
                    right: 16px !important;
                    margin: 0 auto;
                    border-radius: 16px;
                    padding: 20px 16px 14px;
                  }

                  .kt-explanation-text {
                    font-size: 15px;
                    line-height: 1.6;
                    margin-top: 12px;
                    margin-bottom: 10px;
                  }

                  .kt-explanation-box--chips .kt-explanation-text {
                    text-align: left;
                    margin-top: 12px;
                  }

                  .kt-skip-all-btn {
                    top: 10px;
                    right: 10px;
                    font-size: 13px;
                    padding: 8px;
                    min-height: 36px;
                    min-width: 36px;
                  }

                  .kt-explanation-nav {
                    margin-top: 12px;
                    padding-top: 12px;
                    gap: 10px;
                  }

                  .kt-nav-btn {
                    font-size: 15px;
                    padding: 10px 16px;
                    min-height: 48px;
                    border-radius: 8px;
                  }

                  /* Hide arrows on mobile for cleaner look */
                  .kt-explanation-arrow {
                    display: none;
                  }
                }

                /* Small mobile styles */
                @media (max-width: 480px) {
                  .kt-explanation-box {
                    min-width: calc(100vw - 24px);
                    max-width: calc(100vw - 24px);
                    width: calc(100vw - 24px);
                    left: 12px !important;
                    right: 12px !important;
                    padding: 18px 14px 12px;
                  }

                  .kt-explanation-text {
                    font-size: 14px;
                  }

                  .kt-nav-btn {
                    font-size: 14px;
                    padding: 10px 14px;
                  }
                }
              `}</style>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: 'rgba(255,255,255,0.02)', borderRadius: 8, overflow: 'hidden' }}>
                <thead>
                  <tr style={{ background: 'rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.9)', minWidth: '300px', verticalAlign: 'top' }}>Game</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.9)', maxWidth: '150px', width: '150px', verticalAlign: 'top' }}>Genre</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.9)', whiteSpace: 'nowrap', verticalAlign: 'top' }}>Price</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.9)', whiteSpace: 'nowrap', verticalAlign: 'top' }}>Ratings</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.9)', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span>Revenue</span>
                        <span
                          title="Add-ons revenue included in total"
                          style={{ display: 'inline-flex', alignItems: 'center' }}
                        >
                          <Info
                            size={14}
                            style={{ opacity: 0.8, cursor: 'default' }}
                          />
                        </span>
                      </span>
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.9)', whiteSpace: 'nowrap', verticalAlign: 'top' }}>Downloads</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.9)', whiteSpace: 'nowrap', verticalAlign: 'top' }}>Release On</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSearchResults.map((result, idx) => {
                    const LOCK_PLACEHOLDER = "%UPGRADE_KEYQ%"
                    const revenueRaw = result.revenue ?? result?.analytics?.revenue
                    const revenueIsPlaceholder = revenueRaw === LOCK_PLACEHOLDER
                    // Check if revenue data actually exists (not null, undefined, or empty string)
                    const revenueExists = revenueRaw !== null && revenueRaw !== undefined && revenueRaw !== '' && revenueRaw !== LOCK_PLACEHOLDER
                    const revenueNumeric = typeof revenueRaw === 'number' ? revenueRaw : (typeof revenueRaw === 'string' && revenueRaw !== LOCK_PLACEHOLDER ? Number(String(revenueRaw).replace(/[,\s]/g, '')) : NaN)
                    let revenueFormatted = ''
                    if (revenueIsPlaceholder) {
                      revenueFormatted = `$ 0 M`
                    } else if (!revenueExists || (!isFinite(revenueNumeric) && revenueRaw !== 0)) {
                      // Data is missing or invalid (but 0 is valid)
                      revenueFormatted = `$0.0`
                    } else if (isFinite(revenueNumeric)) {
                      // Data exists, format it appropriately
                      if (revenueNumeric >= 1000000000) {
                        revenueFormatted = `$ ${(revenueNumeric / 1000000000).toFixed(1)} B`
                      } else if (revenueNumeric >= 1000000) {
                        revenueFormatted = `$ ${(revenueNumeric / 1000000).toFixed(1)} M`
                      } else if (revenueNumeric >= 1000) {
                        revenueFormatted = `$ ${(revenueNumeric / 1000).toFixed(1)} K`
                      } else {
                        revenueFormatted = `$ ${revenueNumeric.toFixed(1)}`
                      }
                    } else {
                      revenueFormatted = `$0.0`
                    }

                    const downloadsRaw = result.downloads ?? result?.stats?.downloads
                    const downloadsIsPlaceholder = downloadsRaw === LOCK_PLACEHOLDER
                    const downloadsNumeric = typeof downloadsRaw === 'number' ? downloadsRaw : (typeof downloadsRaw === 'string' && downloadsRaw !== LOCK_PLACEHOLDER ? Number(String(downloadsRaw).replace(/[,\s]/g, '')) : NaN)
                    
                    let downloadsDisplayFormatted = '0'
                    if (downloadsIsPlaceholder) {
                      downloadsDisplayFormatted = '0 M'
                    } else if (isFinite(downloadsNumeric)) {
                      if (downloadsNumeric >= 1000000000) {
                        downloadsDisplayFormatted = `${(downloadsNumeric / 1000000000).toFixed(1)} B`
                      } else if (downloadsNumeric >= 1000000) {
                        downloadsDisplayFormatted = `${(downloadsNumeric / 1000000).toFixed(1)} M`
                      } else if (downloadsNumeric >= 1000) {
                        downloadsDisplayFormatted = `${(downloadsNumeric / 1000).toFixed(1)} K`
                      } else {
                        downloadsDisplayFormatted = `${Math.round(downloadsNumeric)}`
                      }
                    } else {
                      downloadsDisplayFormatted = downloadsRaw !== null && downloadsRaw !== undefined ? String(downloadsRaw) : '0'
                    }

                    return (
                      <tr
                        key={result.item_id || idx}
                        style={{
                          borderBottom: '1px solid rgba(255,255,255,0.05)',
                          cursor: 'pointer',
                          transition: 'background 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        onClick={() => openAppDetail(result)}
                      >
                        <td style={{ padding: '12px 16px', maxWidth: '300px', width: '300px', verticalAlign: 'top', textAlign: 'left' }}>
                          <div style={{ width: '100%', overflow: 'hidden' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, width: '100%', minWidth: 0 }}>
                              <img
                                src={result.image_url || minecraftImg}
                                alt={result.name}
                                className="ksr-table-game-image"
                                style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
                              />
                              <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', width: '100%' }}>
<div
  style={{
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 4
  }}
  title={result.name}
>
  {result.name || ''}
</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  {(() => {
                                    const plat = (result.platform || result.platforms || "").toString().toLowerCase()
                                    if (!plat) return null

                                    const buildMetaUrl = () => {
                                      if (result.platform_url) return result.platform_url
                                      const slug = result.meta_slug || result.slug || result.experience_slug || result.experience || result.app_slug
                                      const id = result.item_id || result.id || result.app_id
                                      if (slug && id) return `https://www.meta.com/experiences/${slug}/${id}`
                                      if (id) return `https://www.meta.com/experiences/${id}`
                                      if (slug) return `https://www.meta.com/experiences/${slug}`
                                      return `https://www.meta.com/search/?q=${encodeURIComponent(result.name || '')}`
                                    }

                                    const buildSteamUrl = () => {
                                      if (result.platform_url) return result.platform_url
                                      // item_id is prefixed with its platform, e.g. "steam_639460"
                                      const id = String(result.steam_appid || result.steam_app_id || result.steamappid || result.app_id || result.item_id || '').replace(/^steam_/i, '')
                                      if (/^\d+$/.test(id)) return `https://store.steampowered.com/app/${id}`
                                      return `https://store.steampowered.com/search/?term=${encodeURIComponent(result.name || '')}`
                                    }

                                    const buildAppleUrl = () => {
                                      if (result.platform_url) return result.platform_url
                                      // item_id is prefixed with its platform, e.g. "apple_vision_6478746501"
                                      const id = String(result.apple_item_id || result.apple_id || result.app_id || result.item_id || '').replace(/^apple_vision_/i, '')
                                      if (/^\d+$/.test(id)) return `https://apps.apple.com/app/id${id}`
                                      return `https://apps.apple.com/us/search?term=${encodeURIComponent(result.name || '')}`
                                    }

                                    const buildPicoUrl = () => {
                                      if (result.platform_url) return result.platform_url
                                      // item_id is prefixed with its platform, e.g. "pico_7472678219464949801"
                                      const id = String(result.item_id || result.app_id || '').replace(/^pico_/i, '')
                                      if (/^\d+$/.test(id)) return `https://store-global.picoxr.com/global/detail/1/${id}`
                                      return `https://www.google.com/search?q=${encodeURIComponent((result.name || '') + ' site:store-global.picoxr.com')}`
                                    }

                                    const getPlatformUrl = () => {
                                      if (plat.includes('meta') || plat.includes('oculus') || plat.includes('quest')) return buildMetaUrl()
                                      if (plat.includes('steam')) return buildSteamUrl()
                                      if (plat.includes('apple')) return buildAppleUrl()
                                      if (plat.includes('pico')) return buildPicoUrl()
                                      return result.platform_url || `https://www.google.com/search?q=${encodeURIComponent((result.name || '') + ' ' + plat)}`
                                    }

                                    const href = getPlatformUrl()

                                    if (plat.includes("meta")) {
                                      return (
                                        <>
                                          <a
                                            href={href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ display: "inline-block" }}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <PlatformIcon platform="meta" style={{ height: 16, width: "auto" }} />
                                          </a>
                                          {result.publisher && (
                                            <span
                                              style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                height: 16,
                                                padding: "0 6px",
                                                fontSize: 10,
                                                fontWeight: 500,
                                                color: "#fff",
                                                background: "rgba(255, 255, 255, 0.15)",
                                                borderRadius: 4,
                                                whiteSpace: "nowrap",
                                                maxWidth: "80px",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis"
                                              }}
                                              title={result.publisher}
                                            >
                                              {result.publisher}
                                            </span>
                                          )}
                                        </>
                                      )
                                    }

                                    if (plat.includes("steam")) {
                                      return (
                                        <a
                                          href={href}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{ display: "inline-block", color: "#fff" }}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <PlatformIcon platform="steam" style={{ fontSize: 14, color: "#fff" }} />
                                        </a>
                                      )
                                    }

                                    if (plat.includes("apple")) {
                                      return (
                                        <a
                                          href={href}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{ display: "inline-block", color: "#fff" }}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <PlatformIcon platform="apple_vision" style={{ fontSize: 14, color: "#fff" }} />
                                        </a>
                                      )
                                    }

                                    if (plat.includes("pico")) {
                                      return (
                                        <a
                                          href={href}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{ display: "inline-block", color: "#fff" }}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <PlatformIcon platform="pico" style={{ fontSize: 14, color: "#fff" }} />
                                        </a>
                                      )
                                    }

                                    return null
                                  })()}
                                </div>
                              </div>
                            </div>
                            {/* Analytics metrics row */}
                            {result.analytics && typeof result.analytics === "object" && (
                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  flexWrap: "wrap",
                                  alignItems: "center",
                                  marginTop: 8
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {(() => {
                                  const analyticsKeys = Object.keys(result.analytics)
                                  const metricsToShow = []
                                  let remainingCount = 0

                                  // Check for ktr and ku first (case-insensitive)
                                  const ktrMetric = analyticsKeys.find(key => {
                                    const norm = key.toString().toLowerCase().replace(/[_-]/g, ' ').trim()
                                    return norm === 'ktr' || norm.includes('ktr')
                                  })
                                  const kuMetric = analyticsKeys.find(key => {
                                    const norm = key.toString().toLowerCase().replace(/[_-]/g, ' ').trim()
                                    return norm === 'ku' && !norm.includes('ktr')
                                  })

                                  if (ktrMetric) metricsToShow.push(ktrMetric)
                                  if (kuMetric) metricsToShow.push(kuMetric)

                                  // Count remaining metrics (excluding ktr and ku)
                                  remainingCount = analyticsKeys.filter(key => {
                                    const norm = key.toString().toLowerCase().replace(/[_-]/g, ' ').trim()
                                    return !metricsToShow.includes(key) && norm !== 'ktr' && norm !== 'ku' && !norm.includes('ktr') && !norm.includes('ku')
                                  }).length

                                  return (
                                    <>
                                      {metricsToShow.map((metric) => {
                                        const baseStyle = {
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 8,
                                          padding: '6px 8px',
                                          fontSize: 13,
                                          borderRadius: 8,
                                          background: 'rgba(255,255,255,0.02)',
                                          boxShadow: 'inset 0 -1px 0 rgba(0,0,0,0.2)',
                                          cursor: 'pointer',
                                          flexShrink: 0,
                                          whiteSpace: 'nowrap',
                                          color: '#c6c9cc',
                                          fontWeight: 600,
                                          position: 'relative',
                                          zIndex: 10
                                        }

                                        // Determine tooltip based on metric type
                                        const metricNorm = metric.toString().toLowerCase().replace(/[_-]/g, ' ').trim()
                                        let tooltipText = metric
                                        // Check if this is the KTR metric
                                        if (metric === ktrMetric || metricNorm === 'ktr' || metricNorm.includes('ktr')) {
                                          tooltipText = 'Keyword Relevance'
                                        }
                                        // Check if this is the KU metric (and not KTR)
                                        else if (metric === kuMetric || (metricNorm === 'ku' && !metricNorm.includes('ktr'))) {
                                          tooltipText = 'Keyword Usage'
                                        }

                                        return (
                                          <span
                                            key={metric}
                                            className="ksr-analytics-faux highlight"
                                            role="button"
                                            aria-disabled="true"
                                            title={tooltipText}
                                            style={baseStyle}
                                          >
                                            <span className="ksr-analytics-name">{metric}</span>
                                            <span className="ksr-analytics-arrow" style={{ opacity: 0.9 }}>→</span>
                                          </span>
                                        )
                                      })}

                                      {remainingCount > 0 && (
                                        <button
                                          aria-label={`View ${remainingCount} more metrics for ${result.name}`}
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            openAppDetail(result)
                                          }}
                                          title={`View ${remainingCount} more metrics in Custom Analytics`}
                                          style={{
                                            background: "rgba(246,37,90,1)",
                                            border: "none",
                                            color: "#fff",
                                            cursor: "pointer",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 4,
                                            padding: "4px 8px",
                                            fontSize: 11,
                                            fontWeight: 700,
                                            borderRadius: 999,
                                            boxShadow: "0 4px 12px rgba(246,37,90,0.14)",
                                            transition: "transform 120ms ease, box-shadow 120ms ease",
                                            flexShrink: 0,
                                          }}
                                          onMouseDown={(e) => e.currentTarget.style.transform = 'translateY(1px)'}
                                          onMouseUp={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                        >
                                          <span>View More</span>
                                        </button>
                                      )}
                                    </>
                                  )
                                })()}
                              </div>
                            )}
                          </div>
                        </td>
                        <td
                          style={{ padding: '12px 16px', verticalAlign: 'top', maxWidth: '150px', width: '150px', textAlign: 'left' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {result.genre && result.genre.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {result.genre.length === 1 ? (
                                <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.9)' }}>{result.genre[0]}</span>
                              ) : (() => {
                                const isExpanded = expandedGenres.has(result.item_id || idx)
                                const shouldShowMore = result.genre.length > 2

                                return (
                                  <>
                                    <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.9)' }}>{result.genre[0]},</span>
                                    <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.9)' }}>{result.genre[1]}</span>
                                    {shouldShowMore && (
                                      <>
                                        {isExpanded ? (
                                          <>
                                            {result.genre.slice(2).map((genre, genreIdx) => (
                                              <span key={genreIdx} style={{ fontSize: 14, color: 'rgba(255,255,255,0.9)' }}>{genre}{genreIdx < result.genre.slice(2).length - 1 ? ',' : ''}</span>
                                            ))}
                                            <span
                                              style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', cursor: 'pointer', textDecoration: 'underline' }}
                                              onClick={() => {
                                                const newExpanded = new Set(expandedGenres)
                                                newExpanded.delete(result.item_id || idx)
                                                setExpandedGenres(newExpanded)
                                              }}
                                            >
                                              Show less
                                            </span>
                                          </>
                                        ) : (
                                          <span
                                            style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', cursor: 'pointer', textDecoration: 'underline' }}
                                            onClick={() => {
                                              const newExpanded = new Set(expandedGenres)
                                              newExpanded.add(result.item_id || idx)
                                              setExpandedGenres(newExpanded)
                                            }}
                                          >
                                            +{result.genre.length - 2} more
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </>
                                )
                              })()}
                            </div>
                          ) : (
                            <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)' }}>N/A</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', color: 'rgba(255,255,255,0.8)', fontSize: 14, textAlign: 'right', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                          <span style={{ display: 'block', textAlign: 'right' }}>$ {result.price?.amount || 0}</span>
                        </td>
                        <td style={{ padding: '12px 16px', verticalAlign: 'top', textAlign: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                            <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14 }}>{(result.rating?.average || 0).toFixed(1)}</span>
                            <span style={{ color: '#FFD700', fontSize: 14 }}>★</span>
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', color: 'rgba(255,255,255,0.8)', fontSize: 14, textAlign: 'right', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                          {revenueIsPlaceholder ? (
                            <span
                              title="Unlock detailed analytics powered by KeyQuest's proprietary data models."
                              onClick={(e) => {
                                e.stopPropagation()
                                setShowUpgradeModal(true)
                              }}
                              style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", justifyContent: 'flex-end', marginLeft: 'auto' }}
                              role="button"
                            >
                              <span style={{ filter: "blur(4px)", WebkitFilter: "blur(4px)", opacity: 0.6, userSelect: 'none', pointerEvents: 'none' }}>{revenueFormatted}</span>
                              <i className="fas fa-lock" aria-hidden="true" style={{ fontSize: 12, color: '#fff', cursor: 'pointer' }} />
                            </span>
                          ) : (
                            <span style={{ display: 'block', textAlign: 'right' }}>{revenueFormatted}</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', color: 'rgba(255,255,255,0.8)', fontSize: 14, textAlign: 'right', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                          {downloadsIsPlaceholder ? (
                            <span
                              title="Unlock detailed analytics powered by KeyQuest's proprietary data models."
                              onClick={(e) => {
                                e.stopPropagation()
                                setShowUpgradeModal(true)
                              }}
                              style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", justifyContent: 'flex-end' }}
                              role="button"
                            >
                              <span style={{ filter: "blur(4px)", WebkitFilter: "blur(4px)", opacity: 0.6, userSelect: 'none', pointerEvents: 'none' }}>{downloadsDisplayFormatted}</span>
                              <i className="fas fa-lock" aria-hidden="true" style={{ fontSize: 12, color: '#fff', cursor: 'pointer' }} />
                            </span>
                          ) : (
                            <span style={{ display: 'block', textAlign: 'right' }}>{downloadsDisplayFormatted}</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', color: 'rgba(255,255,255,0.7)', fontSize: 14, textAlign: 'left', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                          {result.created_at ? new Date(result.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="ksr-no-results">
              <div className="ksr-no-results-text">
                {isSearchMode && searchValue
                  ? `No results found for "${searchValue}". Try a different search term.`
                  : "No results found for the selected platform."}
              </div>
            </div>
          )}
        </div>
      ) : (
        // Card View (existing)
        <div className="ksr-card-outer row">
          {!hasActiveKeyword ? (
            <div className="ksr-no-results">
              <div className="ksr-no-results-text">
                Start by searching a keyword or selecting a trending keyword to see results.
              </div>
            </div>
          ) : isLoading || isBackendProcessing ? (
            // While RQ/SWR are loading OR backend is in processing state, show skeletons
            Array.from({ length: 3 }).map((_, idx) => <SkeletonCard key={idx} />)
          ) : filteredSearchResults.length > 0 ? (
            filteredSearchResults.map((result, idx) => (
              <div className="col-12 col-lg-12 col-md-6 mb-4">
                <div className="ksr-card" key={idx}>
                  <div
                    className="ksr-card-img-wrap"
                    onClick={() => openAppDetail(result)}
                  >
                    <img
                      src={result.image_url || minecraftImg}
                      alt={result.name}
                      className="ksr-card-img"
                    />
                  </div>

                  <div className="ksr-card-content" style={{ minWidth: 0, overflow: 'visible' }}>
                    <div className="ksr-card-title" style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, overflow: 'hidden' }}>
                      <div
                        className="ksr-card-title-text"
                        title={result.name}
                        style={{
                          flex: '0 1 auto',
                          maxWidth: '65%',
                          minWidth: 0,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontSize: '1.05rem',
                          fontWeight: 600,
                          color: '#fff',
                          lineHeight: '1.2',
                          marginRight: 6,
                        }}
                      >
                        {result.name}
                      </div>

                      {/* platform icon block - kept separate so the ellipsis won't hide the icon */}
                      <div className="ksr-platform-icon" style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}>
                        {(() => {
                          const plat = (result.platform || result.platforms || "").toString().toLowerCase()
                          if (!plat) return null

                          const buildMetaUrl = () => {
                            if (result.platform_url) return result.platform_url
                            const slug = result.meta_slug || result.slug || result.experience_slug || result.experience || result.app_slug
                            const id = result.item_id || result.id || result.app_id
                            if (slug && id) return `https://www.meta.com/experiences/${slug}/${id}`
                            if (id) return `https://www.meta.com/experiences/${id}`
                            if (slug) return `https://www.meta.com/experiences/${slug}`
                            return `https://www.meta.com/search/?q=${encodeURIComponent(result.name || '')}`
                          }

                          const buildSteamUrl = () => {
                            if (result.platform_url) return result.platform_url
                            // item_id is prefixed with its platform, e.g. "steam_639460"
                            const id = String(result.steam_appid || result.steam_app_id || result.steamappid || result.app_id || result.item_id || '').replace(/^steam_/i, '')
                            if (/^\d+$/.test(id)) return `https://store.steampowered.com/app/${id}`
                            return `https://store.steampowered.com/search/?term=${encodeURIComponent(result.name || '')}`
                          }

                          const buildAppleUrl = () => {
                            if (result.platform_url) return result.platform_url
                            // item_id is prefixed with its platform, e.g. "apple_vision_6478746501"
                            const id = String(result.apple_item_id || result.apple_id || result.app_id || result.item_id || '').replace(/^apple_vision_/i, '')
                            if (/^\d+$/.test(id)) return `https://apps.apple.com/app/id${id}`
                            return `https://apps.apple.com/us/search?term=${encodeURIComponent(result.name || '')}`
                          }

                          const buildPicoUrl = () => {
                            if (result.platform_url) return result.platform_url
                            // item_id is prefixed with its platform, e.g. "pico_7472678219464949801"
                            const id = String(result.item_id || result.app_id || '').replace(/^pico_/i, '')
                            if (/^\d+$/.test(id)) return `https://store-global.picoxr.com/global/detail/1/${id}`
                            return `https://www.google.com/search?q=${encodeURIComponent((result.name || '') + ' site:store-global.picoxr.com')}`
                          }

                          const getPlatformUrl = () => {
                            if (plat.includes('meta') || plat.includes('oculus') || plat.includes('quest')) return buildMetaUrl()
                            if (plat.includes('steam')) return buildSteamUrl()
                            if (plat.includes('apple')) return buildAppleUrl()
                            if (plat.includes('pico')) return buildPicoUrl()
                            return result.platform_url || `https://www.google.com/search?q=${encodeURIComponent((result.name || '') + ' ' + plat)}`
                          }

                          const href = getPlatformUrl()

                          if (plat.includes("meta")) {
                            return (
                              <a href={href} target="_blank" rel="noopener noreferrer" style={{ display: "inline-block" }}>
                                <PlatformIcon platform="meta" style={{ height: 16, width: "auto" }} />
                              </a>
                            )
                          }

                          if (plat.includes("steam")) {
                            return (
                              <a href={href} target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", color: "#fff" }}>
                                <PlatformIcon platform="steam" style={{ fontSize: 16, verticalAlign: "middle" }} />
                              </a>
                            )
                          }

                          if (plat.includes("apple")) {
                            return (
                              <a href={href} target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", color: "#fff" }}>
                                <PlatformIcon platform="apple_vision" style={{ fontSize: 16, verticalAlign: "middle" }} />
                              </a>
                            )
                          }

                          if (plat.includes("pico")) {
                            return (
                              <a href={href} target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", color: "#fff" }}>
                                <PlatformIcon platform="pico" style={{ fontSize: 16, verticalAlign: "middle" }} />
                              </a>
                            )
                          }

                          return null
                        })()}
                      </div>
                    </div>
                    {/* Analytics metric names (only keys) with arrow linking to tracking tab */}
                    {result.analytics && typeof result.analytics === "object" && (
                      <div
                        className="ksr-analytics-row"
                        style={{
                          marginTop: 8,
                          display: "flex",
                          gap: 8,
                          flexWrap: "nowrap",
                          alignItems: "center",
                          whiteSpace: 'nowrap',
                          overflow: 'visible',
                        }}
                      >
                        <button
                          key="unlock_custom_metrics"
                          className="ksr-analytics-cta"
                          aria-label={`Open tracking for ${result.name}`}
                          onClick={() => openAppDetail(result)}
                          title={
                            "Unlock detailed analytics powered by KeyQuest's proprietary data models."
                          }
                          style={{
                            background: "rgba(246,37,90,1)",
                            border: "none",
                            color: "#fff",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "6px 10px",
                            fontSize: 13,
                            fontWeight: 700,
                            borderRadius: 999,
                            boxShadow: "0 6px 18px rgba(246,37,90,0.14)",
                            transition: "transform 120ms ease, box-shadow 120ms ease",
                            flexShrink: 0,
                          }}
                          onMouseDown={(e) => e.currentTarget.style.transform = 'translateY(1px)'}
                          onMouseUp={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                              <path d="M12 2C9.243 2 7 4.243 7 7v3H6a2 2 0 00-2 2v7a2 2 0 002 2h12a2 2 0 002-2v-7a2 2 0 00-2-2h-1V7c0-2.757-2.243-5-5-5z" fill="#fff" opacity="0.95" />
                            </svg>
                            <span>Unlock custom metrics</span>
                          </span>
                        </button>

                        {(() => {
                          const analyticsKeys = Object.keys(result.analytics)
                          const metricsToShow = []
                          let remainingCount = 0

                          // Check for ktr and ku first (case-insensitive)
                          const ktrMetric = analyticsKeys.find(key => {
                            const norm = key.toString().toLowerCase().replace(/[_-]/g, ' ').trim()
                            return norm === 'ktr' || norm.includes('ktr')
                          })
                          const kuMetric = analyticsKeys.find(key => {
                            const norm = key.toString().toLowerCase().replace(/[_-]/g, ' ').trim()
                            return norm === 'ku' && !norm.includes('ktr')
                          })

                          if (ktrMetric) metricsToShow.push(ktrMetric)
                          if (kuMetric) metricsToShow.push(kuMetric)

                          // Count remaining metrics (excluding ktr and ku)
                          remainingCount = analyticsKeys.filter(key => {
                            const norm = key.toString().toLowerCase().replace(/[_-]/g, ' ').trim()
                            return !metricsToShow.includes(key) && norm !== 'ktr' && norm !== 'ku' && !norm.includes('ktr') && !norm.includes('ku')
                          }).length

                          return (
                            <>
                              {metricsToShow.map((metric) => {
                                const baseStyle = {
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  padding: '6px 8px',
                                  fontSize: 13,
                                  borderRadius: 8,
                                  background: 'rgba(255,255,255,0.02)',
                                  boxShadow: 'inset 0 -1px 0 rgba(0,0,0,0.2)',
                                  cursor: 'pointer',
                                  flexShrink: 0,
                                  whiteSpace: 'nowrap',
                                  color: '#c6c9cc',
                                  fontWeight: 600
                                }

                                // Determine tooltip based on metric type
                                // Use the same normalization logic as when finding the metrics
                                const metricNorm = metric.toString().toLowerCase().replace(/[_-]/g, ' ').trim()
                                let tooltipText = metric
                                // Check if this is the KTR metric
                                if (metric === ktrMetric || metricNorm === 'ktr' || metricNorm.includes('ktr')) {
                                  tooltipText = 'Keyword Relevance'
                                }
                                // Check if this is the KU metric (and not KTR)
                                else if (metric === kuMetric || (metricNorm === 'ku' && !metricNorm.includes('ktr'))) {
                                  tooltipText = 'Keyword Usage'
                                }

                                return (
                                  <span
                                    key={metric}
                                    className="ksr-analytics-faux highlight"
                                    role="button"
                                    aria-disabled="true"
                                    title={tooltipText}
                                    style={{
                                      ...baseStyle,
                                      position: 'relative',
                                      zIndex: 10
                                    }}
                                  >
                                    <span className="ksr-analytics-name">{metric}</span>
                                    <span className="ksr-analytics-arrow" style={{ opacity: 0.9 }}>→</span>
                                  </span>
                                )
                              })}

                              {remainingCount > 0 && (
                                <span
                                  className="ksr-analytics-faux blurred"
                                  role="button"
                                  aria-disabled="true"
                                  title={`${remainingCount} more metrics`}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '6px 8px',
                                    fontSize: 13,
                                    borderRadius: 8,
                                    background: 'rgba(255,255,255,0.02)',
                                    boxShadow: 'inset 0 -1px 0 rgba(0,0,0,0.2)',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                    whiteSpace: 'nowrap',
                                    color: 'rgba(198,201,204,0.92)',
                                    filter: 'blur(4px)',
                                    WebkitFilter: 'blur(4px)',
                                    opacity: 0.6,
                                    fontWeight: 600
                                  }}
                                >
                                  <span className="ksr-analytics-name">+{remainingCount}</span>
                                  <span className="ksr-analytics-arrow" style={{ opacity: 0.9 }}>→</span>
                                </span>
                              )}
                            </>
                          )
                        })()}
                      </div>
                    )}
                    <div className="ksr-card-subtitle">{result.developer}</div>
                    <div className="ksr-card-tags" style={{ flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                      {result.genre &&
                        result.genre.map((genre, genreIdx) => (
                          <React.Fragment key={genreIdx}>
                            <span className="ksr-tag" style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>{genre}</span>
                            {genreIdx < result.genre.length - 1 && (
                              <span className="ksr-dot" style={{ flexShrink: 0 }}>•</span>
                            )}
                          </React.Fragment>
                        ))}
                    </div>
                    {/* game_modes will be shown in the info row (same style as Price) */}
                    <div className="ksr-card-info-row" style={{ flexWrap: 'nowrap' }}>
                      <div className="ksr-info-block" style={{ flexShrink: 0 }}>
                        <div className="ksr-info-label" title="Price of the game">Price</div>
                        <div className="ksr-info-value" style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          $ {result.price?.amount || 0}
                        </div>
                      </div>
                      <div className="ksr-info-block" style={{ flexShrink: 0 }}>
                        <div className="ksr-info-label" title="Average rating">Ratings</div>
                        <div className="ksr-info-value" style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                          <span>{(result.rating?.average || 0).toFixed(1)}</span>
                          <span className="ksr-star" style={{ flexShrink: 0 }}>★</span>
                        </div>
                      </div>

                      <div className="ksr-info-block" style={{ flexShrink: 0 }}>
                        <div className="ksr-info-label" title="Game Modes of the Game.">Modes</div>
                        <div className="ksr-info-value" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap', minHeight: '20px', whiteSpace: 'nowrap' }}>
                          {(() => {
                            const modes = Array.isArray(result.game_modes)
                              ? result.game_modes
                              : result.game_modes
                                ? [result.game_modes]
                                : []

                            if (modes.length === 0) {
                              return <span>N/A</span>
                            }

                            // Convert modes to lowercase for checking
                            const modesLower = modes.map(m => m.toString().toLowerCase().trim().replace(/[_\s-]/g, ''))

                            // Check for each mode type in fixed order: Single User, Multiplayer, Co-op
                            const hasSingleUser = modesLower.some(m => m.includes('single'))
                            const hasMultiplayer = modesLower.some(m => m.includes('multiplayer') || m.includes('multi'))
                            const hasCoop = modesLower.some(m => m.includes('coop') || m.includes('cooperative'))

                            // Find the original mode strings for tooltips
                            const singleUserMode = modes.find(m => m.toString().toLowerCase().trim().replace(/[_\s-]/g, '').includes('single'))
                            const multiplayerMode = modes.find(m => {
                              const mLower = m.toString().toLowerCase().trim().replace(/[_\s-]/g, '')
                              return mLower.includes('multiplayer') || mLower.includes('multi')
                            })
                            const coopMode = modes.find(m => {
                              const mLower = m.toString().toLowerCase().trim().replace(/[_\s-]/g, '')
                              return mLower.includes('coop') || mLower.includes('cooperative')
                            })

                            const iconComponents = []

                            // Always display in order: Single User, Multiplayer, Co-op
                            if (hasSingleUser) {
                              iconComponents.push(
                                <span
                                  key="single-user"
                                  title={singleUserMode || 'Single User'}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    lineHeight: 1,
                                    flexShrink: 0
                                  }}
                                >
                                  <SingleUserIcon size={18} color="#D2D2D2" />
                                </span>
                              )
                            }

                            if (hasMultiplayer) {
                              iconComponents.push(
                                <span
                                  key="multiplayer"
                                  title={multiplayerMode || 'Multiplayer'}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    lineHeight: 1,
                                    flexShrink: 0
                                  }}
                                >
                                  <MultiplayerIcon size={18} color="#D2D2D2" />
                                </span>
                              )
                            }

                            if (hasCoop) {
                              iconComponents.push(
                                <span
                                  key="co-op"
                                  title={coopMode || 'Co-op'}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    lineHeight: 1,
                                    flexShrink: 0
                                  }}
                                >
                                  <CoopIcon size={18} color="#D2D2D2" />
                                </span>
                              )
                            }

                            return iconComponents.length > 0 ? iconComponents : <span>N/A</span>
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row row-cols-2 row-cols-lg-1 mt-lg-0 mt-2">
                    <div className="col text-lg-end">
                      <div className="ksr-side-label mb-3" title="Total Revenue of the Game.">
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span>Total Revenue</span>
                          <span
                            title="Add-ons revenue included in total"
                            style={{ display: 'inline-flex', alignItems: 'center' }}
                          >
                            <Info
                              size={14}
                              style={{ opacity: 0.8, cursor: 'default' }}
                            />
                          </span>
                        </span>
                      </div>
                      <div className="ksr-side-value mb-3 mb-lg-0">
                        {(() => {
                          const LOCK_PLACEHOLDER = "%UPGRADE_KEYQ%"
                          const raw = result.revenue ?? result?.analytics?.revenue
                          const isPlaceholder = raw === LOCK_PLACEHOLDER
                          // Check if revenue data actually exists (not null, undefined, or empty string)
                          const revenueExists = raw !== null && raw !== undefined && raw !== '' && raw !== LOCK_PLACEHOLDER
                          const numeric = typeof raw === 'number' ? raw : (typeof raw === 'string' && raw !== LOCK_PLACEHOLDER ? Number(String(raw).replace(/[,\s]/g, '')) : NaN)
                          let formatted = ''
                          if (isPlaceholder) {
                            formatted = `$ 0 M`
                          } else if (!revenueExists || (!isFinite(numeric) && raw !== 0)) {
                            // Data is missing or invalid (but 0 is valid)
                            formatted = `$0.0`
                          } else if (isFinite(numeric)) {
                            // Data exists, format it appropriately
                            if (numeric >= 1000000000) {
                              formatted = `$ ${(numeric / 1000000000).toFixed(1)} B`
                            } else if (numeric >= 1000000) {
                              formatted = `$ ${(numeric / 1000000).toFixed(1)} M`
                            } else if (numeric >= 1000) {
                              formatted = `$ ${(numeric / 1000).toFixed(1)} K`
                            } else {
                              formatted = `$ ${numeric.toFixed(1)}`
                            }
                          } else {
                            formatted = `$0.0`
                          }
                          const shouldLock = isPlaceholder

                          return shouldLock ? (
                            <span
                              title={
                                "Unlock detailed analytics powered by KeyQuest's proprietary data models."
                              }
                              onClick={() => setShowUpgradeModal(true)}
                              style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", whiteSpace: 'nowrap' }}
                              role="button"
                            >
                              <span style={{ filter: "blur(4px)", WebkitFilter: "blur(4px)" }}>
                                {formatted}
                              </span>
                              <i className="fas fa-lock" aria-hidden="true" style={{ fontSize: 12, color: '#fff', flexShrink: 0 }} />
                            </span>
                          ) : (
                            <span style={{ whiteSpace: 'nowrap' }}>{formatted}</span>
                          )
                        })()}
                      </div>
                    </div>
                    <div className="col text-end">
                      <div className="ksr-side-label mb-3" title="Total Downloads of the Game.">Estimated Downloads</div>
                      <div className="ksr-side-value">
                        {(() => {
                          const LOCK_PLACEHOLDER = "%UPGRADE_KEYQ%"
                          const raw = result.downloads ?? result?.stats?.downloads
                          const isPlaceholder = raw === LOCK_PLACEHOLDER
                          const numeric = typeof raw === 'number' ? raw : (typeof raw === 'string' && raw !== LOCK_PLACEHOLDER ? Number(String(raw).replace(/[,\s]/g, '')) : NaN)
                          const display = isFinite(numeric) ? numeric : (isPlaceholder ? 0 : (raw || 0))
                          const shouldLock = isPlaceholder

                          return shouldLock ? (
                            <span
                              title={
                                "Unlock detailed analytics powered by KeyQuest’s proprietary data models."
                              }
                              onClick={() => setShowUpgradeModal(true)}
                              style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}
                              role="button"
                            >
                              <span style={{ filter: "blur(4px)", WebkitFilter: "blur(4px)" }}>
                                {display}
                              </span>
                              <i className="fas fa-lock" aria-hidden="true" style={{ fontSize: 12, color: '#fff' }} />
                            </span>
                          ) : (
                            display
                          )
                        })()}
                      </div>
                    </div>
                    <div className="col-12 text-lg-end">
                      <div className="ksr-side-label">
                        Release On :{" "}
                        <span className="ksr-side-value">
                          {result.created_at
                            ? new Date(result.created_at).toLocaleDateString()
                            : "N/A"}
                        </span>{" "}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="ksr-no-results">
              <div className="ksr-no-results-text">
                {isSearchMode && searchValue
                  ? `No results found for "${searchValue}". Try a different search term.`
                  : "No results found for the selected platform."}
              </div>
            </div>
          )}
        </div>
      )}
      {hasActiveKeyword && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          isLoading={isLoading}
        />
      )}
      {/* Subscription / Upgrade modal (opened when clicking lock) */}
      <AppsTrackingPlanModal
        show={showUpgradeModal}
        handleClose={() => setShowUpgradeModal(false)}
        title=" Upgrade your plan to unlock this feature"
      />
    </div>
  )
}

export default KeywordSearchResults
